import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

import KogModalSideComponent from "@/main/components/modal/ModalSide";
import KOGModalConfirm from "@/main/components/shared/KogmodalConfirm/modalConfirm";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";

import { KeyValue } from "@/main/models/common";
import { DistributionRegionResponse } from "@/main/models/response";
import { DistributionRegionSaveRequest } from "@/main/models/request";
import { distributionRegionApi } from "@/main/api";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import {
	getDefaultOptionDrop,
	getTranslateByFullKey,
	getTranslateByKey,
	isNullUndefined,
	removeEmptyValues,
} from "@/main/services/utils";
import { KOGAreaButtons } from "@/main/components/shared/styles";

import * as S from "./styles";

const INITIAL_FORM_STATE_DEFAULT = {
	country: null as KeyValue | null,
	regionName: "",
	zipCodeStart: "",
	zipCodeEnd: "",
};

type IFormFiltersRegion = typeof INITIAL_FORM_STATE_DEFAULT;

interface IModalRegionProps {
	isOpenModal: boolean;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	itemRegion?: DistributionRegionResponse;
	updateRowDataGrid?: (row: DistributionRegionResponse) => void;
	refreshSearchGrid?: (guidRegion: string) => void;
	closeModal: () => void;
}

function ModalRegion({
	isOpenModal,
	itemRegion,
	updateRowDataGrid = () => {},
	refreshSearchGrid = () => {},
	closeModal,
}: Readonly<IModalRegionProps>) {
	const isEdit = !isNullUndefined(itemRegion);
	const dispatch = useDispatch();
	const methods = useForm<IFormFiltersRegion>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE_DEFAULT,
	});

	const { control, handleSubmit, setValue, setError, clearErrors, watch } = methods;

	const { countriesUser: countryReducer } = useCountryReducer();
	const optionDefaultCountry = isEdit ? null : getDefaultOptionDrop(countryReducer.data);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [isOpenConfirmEditModal, setIsOpenConfirmEditModal] = useState<boolean>(false);
	const [formDataPendingEdit, setFormDataPendingEdit] = useState<IFormFiltersRegion | null>(null);

	function getCountryFormValue(): KeyValue | null {
		if (isNullUndefined(itemRegion?.guidCountry)) return null;

		const countryFromOptions = countryReducer.data?.find(
			(country) => String(country.key) === String(itemRegion.guidCountry),
		);

		if (!isNullUndefined(countryFromOptions)) return countryFromOptions;

		if (isNullUndefined(itemRegion?.countryName)) {
			return {
				key: itemRegion.guidCountry,
				value: "",
			};
		}

		const countryValue = String(itemRegion.countryName).toLocaleLowerCase().startsWith("t.")
			? getTranslateByFullKey(String(itemRegion.countryName))
			: String(itemRegion.countryName);

		return {
			key: itemRegion.guidCountry,
			value: countryValue,
		};
	}

	function loadEditRegion() {
		if (isNullUndefined(itemRegion)) return;

		const backendForFrontend: IFormFiltersRegion = {
			...INITIAL_FORM_STATE_DEFAULT,
			country: getCountryFormValue(),
			regionName: itemRegion.name,
			zipCodeStart: String(itemRegion.zipCodeStart),
			zipCodeEnd: String(itemRegion.zipCodeEnd),
		};

		methods.reset(backendForFrontend);
	}

	function resetForm() {
		methods.reset();
		clearErrors();
		if (optionDefaultCountry) setValue("country", optionDefaultCountry);
	}

	function saveRegion(data: IFormFiltersRegion) {
		setIsLoadingSave(true);

		const request: DistributionRegionSaveRequest = removeEmptyValues({
			guid: isEdit ? itemRegion?.guid : null,
			guidCountry: String(data.country?.key),
			name: data.regionName,
			zipCodeStart: Number(data.zipCodeStart),
			zipCodeEnd: Number(data.zipCodeEnd),
			active: isEdit ? (itemRegion?.active ?? true) : true,
		});

		distributionRegionApi
			.saveRegion(request)
			.then((response) => {
				const result = response.data.result;
				toast.success(getTranslateByKey("information.savedSuccess"));

				if (!isEdit) {
					refreshSearchGrid(String(result.guid));
					resetForm();
				}

				if (isEdit) {
					methods.reset(data);
					const isResultCountryResourceKey = String(result.countryName ?? "")
						.toLocaleLowerCase()
						.startsWith("t.");

					updateRowDataGrid({
						guid: result.guid,
						guidCountry: result.guidCountry ?? String(data.country?.key ?? itemRegion?.guidCountry ?? ""),
						countryName: isResultCountryResourceKey
							? String(data.country?.value ?? itemRegion?.countryName ?? "")
							: (result.countryName ?? String(data.country?.value ?? itemRegion?.countryName ?? "")),
						name: result.name,
						zipCodeStart: result.zipCodeStart,
						zipCodeEnd: result.zipCodeEnd,
						active: result.active,
					});
				}

				closeModal();
				resetForm();
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}

	async function onSubmitSave(data: IFormFiltersRegion) {
		const start = Number(data.zipCodeStart);
		const end = Number(data.zipCodeEnd);

		if (end < start) {
			setError("zipCodeEnd", {
				type: "zipCodeRange",
				message: getTranslateByKey("information.initialLessEndZipcode", true),
			});
			return;
		}

		if (isEdit) {
			setFormDataPendingEdit(data);
			setIsOpenConfirmEditModal(true);
			return;
		}

		saveRegion(data);
	}

	function onCancelEditConfirmModal() {
		setIsOpenConfirmEditModal(false);
		setFormDataPendingEdit(null);
	}

	function onConfirmEditSave() {
		if (isNullUndefined(formDataPendingEdit)) return;

		saveRegion(formDataPendingEdit);
		setFormDataPendingEdit(null);
	}

	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(itemRegion)) {
			loadEditRegion();
		}
	}, [JSON.stringify(itemRegion), JSON.stringify(countryReducer.data)]);

	useEffect(() => {
		if (!isOpenModal) {
			resetForm();
			setIsOpenConfirmEditModal(false);
			setFormDataPendingEdit(null);
		}
	}, [isOpenModal]);

	const titleEditConfirmModal = getTranslateByKey("information.confirmEditRegion").replace(
		"{regionName}",
		String(itemRegion?.name ?? ""),
	);

	const disabledCountry = isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;

	const watcherZipCodeStart = watch("zipCodeStart");
	const watcherZipCodeEnd = watch("zipCodeEnd");

	useEffect(() => {
		if (!watcherZipCodeStart || !watcherZipCodeEnd) {
			clearErrors("zipCodeEnd");
			return;
		}

		const start = Number(watcherZipCodeStart);
		const end = Number(watcherZipCodeEnd);

		if (end >= start) {
			clearErrors("zipCodeEnd");
		}
	}, [watcherZipCodeStart, watcherZipCodeEnd]);

	return (
		<>
			<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
				<S.Container>
					<KogAutoCompleteControlled
						id="id-region-select-country-register"
						nameCustom="country"
						label={getTranslateByKey("information.country")}
						isDefaultValueUnique
						valueDefaultKey={optionDefaultCountry?.key}
						control={control}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						disabled={isEdit || disabledCountry}
						onChangeCustom={() => {}}
					/>

					<KogInputTextControlledNew
						id="id-region-txt-name"
						name="regionName"
						label={getTranslateByKey("information.regionName")}
						control={control}
						controllerProps={{ rules: { required: true, maxLength: 150 } }}
					/>

					<KogInputTextControlledNew
						id="id-region-txt-zipcode-start"
						name="zipCodeStart"
						label={getTranslateByKey("information.initialZipCode")}
						control={control}
						hasOnlyNumber
						inputProps={{ maxLength: 5 }}
						controllerProps={{ rules: { required: true } }}
					/>

					<KogInputTextControlledNew
						id="id-region-txt-zipcode-end"
						name="zipCodeEnd"
						label={getTranslateByKey("information.finalZipCode")}
						control={control}
						hasOnlyNumber
						inputProps={{ maxLength: 5 }}
						controllerProps={{ rules: { required: true } }}
					/>

					<KOGAreaButtons style={{ justifyContent: "center" }}>
						<SecondaryButton id="id-region-btn-save" type="submit" isLoading={isLoadingSave}>
							{getTranslateByKey("information.save")}
						</SecondaryButton>
					</KOGAreaButtons>
				</S.Container>
			</form>

			<KOGModalConfirm
				titleModal={titleEditConfirmModal}
				isOpenModal={isOpenConfirmEditModal}
				setIsOpenModal={setIsOpenConfirmEditModal}
				closeModal={() => setIsOpenConfirmEditModal(false)}
				onCancel={onCancelEditConfirmModal}
				onConfirm={onConfirmEditSave}
			/>
		</>
	);
}

function WrapperModalRegion(props: Readonly<IModalRegionProps>) {
	if (!props.isOpenModal) return null;

	const isEdit = !isNullUndefined(props.itemRegion);
	const titleSaveModal = getTranslateByKey("information.newRegion");
	const titleEditModal = getTranslateByKey("information.editRegion");
	const titleModal = isEdit ? titleEditModal : titleSaveModal;

	function handleCloseModal() {
		props.closeModal();
		props.setIsOpenModal(false);
	}

	return (
		<KogModalSideComponent
			open={props.isOpenModal}
			shouldRenderOpen
			animateOnMount
			title={titleModal}
			closeModal={handleCloseModal}
		>
			<ModalRegion {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalRegion;
